import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, User, LogOut, Menu, X, Search } from "lucide-react";
import { useState } from "react";
import { BRAND_LOGO } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const navItems = [
    { to: "/search", label: "Browse Parts" },
    { to: "/search?category=Engine", label: "Engine" },
    { to: "/search?category=Brakes", label: "Brakes" },
    { to: "/search?category=Electrical", label: "Electrical" },
    { to: "/how-it-works", label: "How it works" },
  ];

  return (
    <>
      <div className="w-full h-1 apa-stripe-accent" data-testid="brand-stripe" />
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-[#E4E4E7]" data-testid="site-header">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 h-20 flex items-center gap-4 lg:gap-8">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo-link">
            <img src={BRAND_LOGO} alt="Auto Parts Africa" className="h-12 w-12 object-contain" />
            <div className="hidden sm:block leading-none">
              <div className="font-display font-extrabold text-[17px] tracking-tight">AUTO PARTS</div>
              <div className="font-display font-extrabold text-[17px] tracking-tight text-[#EF3340]">AFRICA</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-6 ml-4" data-testid="header-nav">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `text-[13px] font-semibold uppercase tracking-wider transition-colors ${
                    isActive ? "text-[#009A44]" : "text-[#121212] hover:text-[#009A44]"
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex-1" />

          <button
            onClick={() => nav("/search")}
            className="p-2 hover:bg-zinc-100 transition-colors"
            aria-label="Search"
            data-testid="header-search-btn"
          >
            <Search size={20} />
          </button>

          <Link to="/cart" className="relative p-2 hover:bg-zinc-100 transition-colors" data-testid="header-cart-link">
            <ShoppingCart size={20} />
            {count > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 bg-[#EF3340] text-white text-[11px] font-bold flex items-center justify-center"
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
          </Link>

          {user && typeof user === "object" ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 border border-[#E4E4E7] px-3 py-2 hover:border-[#121212] transition-colors"
                  data-testid="header-user-menu"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <div className="w-6 h-6 bg-[#009A44] text-white flex items-center justify-center text-xs font-bold">
                      {(user.name || user.email).charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="hidden md:inline text-sm font-semibold max-w-[120px] truncate">
                    {user.name || user.email}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[220px] rounded-none">
                <DropdownMenuLabel>
                  <div className="text-xs text-zinc-500 font-normal">Signed in as</div>
                  <div className="truncate">{user.email}</div>
                  <div className="text-[10px] uppercase tracking-wider mt-1 text-[#009A44] font-bold">
                    {user.role}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-dashboard">
                  My Orders
                </DropdownMenuItem>
                {user.role === "seller" && (
                  <DropdownMenuItem onClick={() => nav("/seller")} data-testid="menu-seller">
                    Seller Console
                  </DropdownMenuItem>
                )}
                {user.role === "local_agent" && (
                  <DropdownMenuItem onClick={() => nav("/agent")} data-testid="menu-agent">
                    Verification Queue
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                    Admin Console
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut size={14} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden sm:flex items-center gap-2">
              <Link to="/login" className="text-sm font-semibold uppercase tracking-wider hover:text-[#009A44]" data-testid="header-login">
                Log in
              </Link>
              <Link to="/register" className="btn-primary !py-2.5 !px-4 text-xs" data-testid="header-register">
                <User size={14} /> Sign up
              </Link>
            </div>
          )}

          <button
            className="lg:hidden p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-btn"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[#E4E4E7] bg-white">
            <div className="px-4 py-4 flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="py-3 border-b border-[#F1F1F2] text-sm font-semibold uppercase tracking-wider"
                >
                  {item.label}
                </Link>
              ))}
              {(!user || user === false) && (
                <div className="flex gap-2 pt-4">
                  <Link to="/login" onClick={() => setOpen(false)} className="btn-outline-dark flex-1">Log in</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="btn-primary flex-1">Sign up</Link>
                </div>
              )}
            </div>
          </div>
        )}
      </header>
    </>
  );
}
